/**
 * Program feed projection — isomorphic (runs on the server to build the wire
 * payload and in the browser to inflate it back into the Emma event shape).
 *
 * The raw Emma events feed for a large meeting (TCT 2025: 661 root events,
 * ~7,000 nodes) is ~17 MB of JSON / ~1.5 MB gzipped. The program guide only
 * reads a small subset of each node, and the heaviest fields (venue objects,
 * faculty objects, links, GUIDs, breadcrumbs, timestamps) are either unused or
 * repeated thousands of times. `projectProgramFeed` keeps only the fields the
 * agenda UI, filters (config/agendaFilters) and text search (lib/textSearch)
 * read, and hoists venues / roles / faculty / session types into lookup tables.
 * `inflateProgramFeed` rebuilds objects in the exact shape the components
 * already expect, so nothing downstream of `useConferenceEvents` changes.
 *
 * Measured on TCT 2025: 17.1 MB -> ~6 MB raw, 1.48 MB -> ~0.6 MB gzipped.
 */

export const PROGRAM_FEED_VERSION = 1

const EVENT_FIELDS = [
    "id",
    "title",
    "datetime_start",
    "datetime_end",
    "day",
    "duration",
    "root_id",
    "parent_id",
    "session_type_id",
    "depth",
    "description",
    "status",
    "addevent_id",
    "is_satellite_event",
]
const PARAM_FIELDS = ["on_demand", "credit", "accreditation_text", "feedback"]
const VENUE_FIELDS = ["id", "name", "breadcrumb", "timezone"]
// Emma varies role.display_name per occurrence (singular/plural by user count) and
// user.highlight per assignment; both are carried on the occurrence, not the table.
const ROLE_FIELDS = ["id", "name", "name_plural", "display_name", "slug"]
const USER_FIELDS = ["id", "okta_id", "first_name", "middle_name", "last_name", "full_name", "degree", "meeting_role"]
const LINK_FIELDS = ["id", "type", "title", "url", "preview_image_url"]
// Only video links (renderLink.js) and abstract links (the program guide's abstract modal) are read
// by the agenda; slide/email/cme links are dropped.
const LINK_TYPES = new Set(["video", "abstract"])

function pick(source, fields) {
    const result = {}
    if (!source) return result
    for (const field of fields) {
        const value = source[field]
        if (value !== undefined && value !== null) result[field] = value
    }
    return result
}

/**
 * @param {Array} events  raw `events.data` array from Emma (depth=all)
 * @param {{meetingId: number, fetchedAt: number}} meta
 */
export function projectProgramFeed(events, { meetingId, fetchedAt }) {
    const tables = { sessionTypes: {}, venues: {}, roles: {}, users: {} }

    function projectEvent(event) {
        const node = pick(event, EVENT_FIELDS)

        const params = pick(event.params, PARAM_FIELDS)
        if (Array.isArray(params.credit) && !params.credit.length) delete params.credit
        if (Object.keys(params).length) node.params = params

        if (event.session_type_id != null && event.session_type) {
            tables.sessionTypes[event.session_type_id] = event.session_type
        }

        if (event.venue?.id != null) {
            tables.venues[event.venue.id] ??= pick(event.venue, VENUE_FIELDS)
            node.venue_id = event.venue.id
        }

        if (event.tracks?.length) node.tracks = event.tracks
        if (event.event_topics?.length) node.event_topics = event.event_topics

        // Each occurrence is [roleId, users, displayName?] where users are ids, or
        // [id, highlight] when highlighted, and displayName is set only when it
        // differs from the table entry.
        const moderators = (event.moderators || []).flatMap(group => {
            if (!group?.role?.id) return []
            const role = (tables.roles[group.role.id] ??= pick(group.role, ROLE_FIELDS))
            const users = (group.users || []).flatMap(user => {
                if (user?.id == null) return []
                tables.users[user.id] ??= pick(user, USER_FIELDS)
                return user.highlight ? [[user.id, user.highlight]] : [user.id]
            })
            const occurrence = [group.role.id, users]
            if (group.role.display_name != null && group.role.display_name !== role.display_name) {
                occurrence.push(group.role.display_name)
            }
            return [occurrence]
        })
        if (moderators.length) node.moderators = moderators

        const links = (event.links || []).filter(link => LINK_TYPES.has(link?.type)).map(link => pick(link, LINK_FIELDS))
        if (links.length) node.links = links

        const children = (event.children || []).map(projectEvent)
        if (children.length) node.children = children

        return node
    }

    return {
        v: PROGRAM_FEED_VERSION,
        meetingId,
        fetchedAt,
        ...tables,
        events: events.map(projectEvent),
    }
}

/**
 * Rebuilds the Emma event shape from a projected payload. Every occurrence gets
 * its own venue / role / user object (the search code mutates `searchValue` on
 * users and events, so shared references would leak highlights across events).
 */
export function inflateProgramFeed(payload) {
    if (!payload || payload.v !== PROGRAM_FEED_VERSION || !Array.isArray(payload.events)) {
        throw new Error("Unexpected program feed payload")
    }
    const { sessionTypes = {}, venues = {}, roles = {}, users = {} } = payload

    function inflateEvent(node) {
        const event = {
            id: node.id,
            title: node.title ?? "",
            datetime_start: node.datetime_start ?? null,
            datetime_end: node.datetime_end ?? null,
            duration: node.duration ?? null,
            root_id: node.root_id ?? null,
            parent_id: node.parent_id ?? null,
            session_type_id: node.session_type_id ?? null,
            session_type: sessionTypes[node.session_type_id] ?? null,
            depth: node.depth ?? 0,
            description: node.description ?? null,
            status: node.status ?? null,
            addevent_id: node.addevent_id ?? null,
            is_satellite_event: node.is_satellite_event ?? 0,
            params: { on_demand: 0, credit: [], ...(node.params || {}) },
            venue: node.venue_id != null && venues[node.venue_id] ? { ...venues[node.venue_id] } : null,
            tracks: node.tracks ? [...node.tracks] : [],
            event_topics: node.event_topics ? [...node.event_topics] : [],
            moderators: (node.moderators || []).map(([roleId, userRefs, displayName]) => ({
                role: { ...(roles[roleId] || { id: roleId }), ...(displayName != null ? { display_name: displayName } : {}) },
                users: userRefs.flatMap(ref => {
                    const [userId, highlight] = Array.isArray(ref) ? ref : [ref, 0]
                    return users[userId] ? [{ ...users[userId], highlight }] : []
                }),
            })),
            links: node.links ? node.links.map(link => ({ ...link })) : [],
            children: (node.children || []).map(inflateEvent),
        }
        if (node.day !== undefined) event.day = node.day
        return event
    }

    return payload.events.map(inflateEvent)
}
